/* eslint-disable no-param-reassign */
import { gql } from '@apollo/client';
import { useAuthContext } from '@authentication/Authentication';
import { useConfirmation } from '@components/modal';
import {
  Graph,
  GraphValidationError,
  useDownloadGraphMutation,
  useEditGraphMutation,
  useGetGraphLastUpdatedLazyQuery,
  useGetGraphMetadataQuery,
  useGraphUpdatedSubscription,
  useValidateGraphMutation,
} from '@generated/graphql';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import { navigate } from 'gatsby';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import { ChannelNode, GraphData, Link, Node } from './types';

interface GraphDataContextType {
  graphData: GraphData;
  graphMetadata: Partial<Graph>;
  graphMetadataLoading: boolean;
  graphParseError: boolean;
  lastCreatedNodeId: string;
  links: Link[];
  nodes: Node[];
  ready: boolean;
  ready$: BehaviorSubject<boolean>;
  validationErrors: GraphValidationError[];
  createNode: (data: ChannelNode, location: [number, number]) => Promise<void>;
  createLink: (data: Pick<Link, 'from' | 'to'>) => Promise<void | { id: string }>;
  duplicateNodes: (nodeId: string[]) => void;
  refetchGraphMetadata: VoidFunction;
  removeSelectedLink: (linkId: string) => void;
  removeSelectedNode: (nodeId: string, save?: boolean) => void;
  removeLink: (id: string) => Promise<void>;
  removeNode: (id: string) => void;
  saveGraph: (dataToSave: GraphData) => Promise<GraphValidationError[]>;
  updateNodeLocation: (id: string, { x, y }: { x: number; y: number }, save?: boolean) => void;
  updateNode: (id: string, newNode: ChannelNode, save: boolean) => void;
  updatePortValue: (
    nodeId: string,
    portId: string,
    value: unknown,
    save?: boolean,
  ) => Promise<void>;
}

export const GraphDataContext = createContext({} as GraphDataContextType);

interface GraphDataProviderProps {
  workspaceId: string;
  graphId: string;
}

gql`
  subscription GraphUpdated($workspaceId: String!, $graphId: String!, $uid: String!) {
    graphUpdates(workspaceId: $workspaceId, graphId: $graphId, uid: $uid) {
      name
      description
      graphId
      graph
    }
  }
`;

export const GraphDataProvider = ({
  children,
  workspaceId,
  graphId,
}: PropsWithChildren<GraphDataProviderProps>) => {
  const { user } = useAuthContext();
  const { showConfirmation } = useConfirmation();
  const { organizationId } = useOrganizationContext();

  const { createAsyncNotification } = useNotifications();
  const [validateGraph] = useValidateGraphMutation({ variables: { workspaceId, graphId } });
  const [getGraphLastUpdated] = useGetGraphLastUpdatedLazyQuery({
    variables: { workspaceId, graphId },
  });
  const [getGraphData] = useDownloadGraphMutation({
    variables: { workspaceId, graphId },
  });
  const [saveGraphMutation] = useEditGraphMutation();
  const {
    data: graphMetadataData,
    loading: graphMetadataLoading,
    refetch: refetchGraphMetadata,
    error: graphMetadataError,
  } = useGetGraphMetadataQuery({
    variables: { workspaceId, graphId },
  });

  const graphMetadata = graphMetadataData?.getGraphs?.[0];

  const [ready, setReady] = useState(false);
  const [graphParseError, setGraphParseError] = useState(false);
  const [graphData, setGraphData] = useState<GraphData>();
  const [nodes, setNodes] = useState<Node[]>([]);
  const [lastNodeId, setLastNodeId] = useState(0);
  const [lastCreatedNodeId, setLastCreatedNodeId] = useState<string>();
  const [links, setLinks] = useState<Link[]>([]);
  const [ready$] = useState<BehaviorSubject<boolean>>(new BehaviorSubject(false));
  const [validationErrors, setValidationErrors] = useState<GraphValidationError[]>([]);

  // Refs mirror state for action methods called from long-lived closures (e.g. d3 drag
  // handlers in DraggableNode whose effect doesn't re-run on every graphData change).
  // Without this, a stale `updateNodeLocation` would rebuild from a snapshot that's
  // missing prior moves, reverting them.
  const graphDataRef = useRef(graphData);
  const nodesRef = useRef(nodes);
  const linksRef = useRef(links);
  const lastNodeIdRef = useRef(lastNodeId);
  useEffect(() => {
    graphDataRef.current = graphData;
  }, [graphData]);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  useEffect(() => {
    linksRef.current = links;
  }, [links]);
  useEffect(() => {
    lastNodeIdRef.current = lastNodeId;
  }, [lastNodeId]);

  const validateGraphData = useCallback(async () => {
    try {
      const { data } = await validateGraph();
      const errors = data?.validateGraph || [];
      setValidationErrors(errors);
      return errors;
    } catch {
      return [];
    }
  }, [validateGraph]);

  const saveGraph = useCallback(
    async (dataToSave: GraphData): Promise<GraphValidationError[]> => {
      if (!dataToSave || graphMetadata?.readOnly) {
        return Promise.resolve([] as GraphValidationError[]);
      }
      try {
        const { data } = await getGraphLastUpdated();
        const lastUpdatedAt = data?.getGraphs?.[0].updatedAt;
        if (lastUpdatedAt && lastUpdatedAt === graphMetadata?.updatedAt) {
          await saveGraphMutation({
            variables: { workspaceId, graphId, graph: JSON.stringify(dataToSave) },
          });
          return await validateGraphData();
        }
        return await Promise.resolve([] as GraphValidationError[]);
      } catch {
        return Promise.resolve([] as GraphValidationError[]);
      }
    },
    [
      graphMetadata?.readOnly,
      graphMetadata?.updatedAt,
      getGraphLastUpdated,
      saveGraphMutation,
      workspaceId,
      graphId,
      validateGraphData,
    ],
  );

  const updateGraphUI = useCallback(
    (result: string) => {
      const { nodes: parsedNodes, version: parsedVersion }: GraphData = JSON.parse(
        result || '',
      ) as GraphData;
      const mutatedLinks: Link[] = [];
      let maxNodeId = 0;
      let save = false;
      try {
        const nodeIdNumbersToFix: string[] = [];
        Object.values(parsedNodes).forEach((parsedNode) => {
          const nodeIdNumber = parseInt(parsedNode.name.split('_').at(-1), 10);
          if (!nodeIdNumber) {
            nodeIdNumbersToFix.push(parsedNode.name);
            save = true;
          }
          maxNodeId = Math.max(maxNodeId, nodeIdNumber || 0);
          Object.entries(parsedNode.links).forEach(([destPort, sources]) => {
            sources.forEach((source) => {
              mutatedLinks.push({
                from: { nodeId: source.sourceNode, portId: `output-${source.outputPort}` },
                to: { nodeId: parsedNode.name, portId: `input-${destPort}` },
                id: crypto.randomUUID(),
              });
            });
          });
          parsedNode.ports.inputs.forEach((input) => {
            if (
              !Object.keys(parsedNode.values).includes(input.name) &&
              !Object.keys(parsedNode.links).includes(input.name)
            ) {
              parsedNode.values[input.name] = input.value ?? input.default ?? null;
              save = true;
            }
            if (
              Object.keys(parsedNode.links).includes(input.name) &&
              parsedNode.links[input.name].length > 0 &&
              Object.keys(parsedNode.values).includes(input.name)
            ) {
              delete parsedNode.values[input.name];
              save = true;
            }
          });

          Object.keys(parsedNode.values).forEach((key) => {
            if (!parsedNode.ports.inputs.some((input) => input.name === key)) {
              delete parsedNode.values[key];
              save = true;
            }
          });
        });
        if (nodeIdNumbersToFix.length > 0) {
          const nodeIdNumberFixMap: Record<string, string> = {};
          nodeIdNumbersToFix.forEach((oldNodeId) => {
            const idParts = oldNodeId.split('_');
            idParts.pop();
            maxNodeId += 1;
            const newId = `${idParts.join('_')}_${maxNodeId}`;
            const newNode = parsedNodes[oldNodeId];
            newNode.name = newId;
            parsedNodes[newId] = { ...parsedNodes[oldNodeId], name: newId };
            delete parsedNodes[oldNodeId];
            nodeIdNumberFixMap[oldNodeId] = newId;
          });
          Object.values(parsedNodes).forEach((node) => {
            Object.values(node.links).forEach((nodeLinks) => {
              nodeLinks.forEach((link) => {
                if (nodeIdNumberFixMap[link.sourceNode]) {
                  link.sourceNode = nodeIdNumberFixMap[link.sourceNode];
                }
              });
            });
          });
          mutatedLinks.forEach((link) => {
            if (nodeIdNumberFixMap[link.from.nodeId]) {
              link.from.nodeId = nodeIdNumberFixMap[link.from.nodeId];
            }
            if (nodeIdNumberFixMap[link.to.nodeId]) {
              link.to.nodeId = nodeIdNumberFixMap[link.to.nodeId];
            }
          });
        }
        setGraphData({ nodes: parsedNodes, version: parsedVersion });
        setNodes(Object.values(parsedNodes));
        setLinks(mutatedLinks);
        setLastNodeId(maxNodeId);
        ready$.next(true);
        setReady(true);
        if (save) {
          void saveGraph({ nodes: parsedNodes, version: parsedVersion });
        } else {
          void validateGraphData();
        }
      } catch (e) {
        console.error('Error parsing graph data', e);
        setGraphParseError(true);
      }
    },
    [ready$, saveGraph, validateGraphData],
  );

  // Stash latest updateGraphUI in a ref so the initial-fetch effect can stay
  // single-run. Re-running it would re-fetch the graph every time saveGraph
  // rebuilds (i.e., whenever graphMetadata changes).
  const updateGraphUIRef = useRef(updateGraphUI);
  useEffect(() => {
    updateGraphUIRef.current = updateGraphUI;
  }, [updateGraphUI]);

  useGraphUpdatedSubscription({
    onData({ data }) {
      updateGraphUI(data?.data?.graphUpdates?.graph);
    },
    onError(error) {
      console.error('Apollo Subscription Error!', error);
    },
    variables: { workspaceId, graphId, uid: user.preferred_username },
    skip: !user,
  });

  useEffect(() => {
    void getGraphData().then((result) => {
      updateGraphUIRef.current?.(result?.data?.downloadGraph);
    });
  }, [getGraphData]);

  useEffect(() => {
    if (graphMetadataError) {
      void navigate(`/${organizationId}/workspaces/${workspaceId}/graphs/`);
    }
  }, [graphMetadataError, organizationId, workspaceId]);

  const createNode = useCallback(
    async (node: ChannelNode, [x, y]: [number, number]) => {
      const newNodeName = `${node.name}_${lastNodeIdRef.current + 1}`;
      setLastNodeId((id) => id + 1);
      const newGraphNode: Node = {
        nodeClass: node.name,
        color: node.color,
        name: newNodeName,
        location: { x, y },
        ports: { inputs: node.inputs, outputs: node.outputs },
        hash: node.hash || node.schemaHash,
        links: {},
        tooltip: node.tooltip,
        values: {},
      };
      newGraphNode.ports.inputs.forEach((input) => {
        newGraphNode.values[input.name] = input.default ?? null;
      });
      setNodes((oldNodes) => [...oldNodes, newGraphNode]);
      const newGraphData = {
        ...graphDataRef.current,
        nodes: {
          ...graphDataRef.current.nodes,
          [newNodeName]: newGraphNode,
        },
      };
      setLastCreatedNodeId(newNodeName);
      setGraphData(newGraphData);
      await saveGraph(newGraphData);
    },
    [saveGraph],
  );

  const onCreateNode = useMemo(
    () =>
      createAsyncNotification(
        'Created new node',
        async (node: ChannelNode, location: [number, number]) => {
          if (process.env.JEST_WORKER_ID === undefined) {
            await createNode(node, location);
          }
        },
      ),
    [createAsyncNotification, createNode],
  );

  const updateNode = useCallback(
    (nodeName: string, newNode: ChannelNode, save = false) => {
      const currentGraphData = graphDataRef.current;
      const oldNode = currentGraphData.nodes[nodeName];
      const newValues = {};
      newNode.inputs.forEach((input) => {
        if (Object.keys(oldNode.links).includes(input.name)) {
          return;
        }
        if (Object.keys(oldNode.values).includes(input.name)) {
          newValues[input.name] = oldNode.values[input.name];
        } else {
          newValues[input.name] = input.default ?? null;
        }
      });

      setNodes((oldNodes) => {
        const newNodes = [...oldNodes];
        const nodeToUpdate = newNodes.find((node) => node.name === nodeName);
        nodeToUpdate.nodeClass = newNode.name;
        nodeToUpdate.color = newNode.color;
        nodeToUpdate.ports = { inputs: newNode.inputs, outputs: newNode.outputs };
        nodeToUpdate.hash = newNode.hash || newNode.schemaHash;
        nodeToUpdate.tooltip = newNode.tooltip;
        nodeToUpdate.values = newValues;
        Object.keys(nodeToUpdate.links).forEach((nodeInput) => {
          if (!newNode.inputs.some((input) => input.name === nodeInput)) {
            delete nodeToUpdate.links[nodeInput];
          }
        });
        return newNodes;
      });
      setLinks((oldLinks) =>
        oldLinks.filter(
          (link) =>
            (link.to.nodeId !== nodeName && link.from.nodeId !== nodeName) ||
            (link.to.nodeId === nodeName &&
              !newNode.inputs.some((input) => input.name === link.to.portId)) ||
            (link.from.nodeId === nodeName &&
              !newNode.outputs.some((output) => output.name === link.from.portId)),
        ),
      );
      const newGraphData = {
        ...currentGraphData,
        nodes: {
          ...currentGraphData.nodes,
          [nodeName]: {
            ...oldNode,
            nodeClass: newNode.name,
            color: newNode.color,
            ports: { inputs: newNode.inputs, outputs: newNode.outputs },
            hash: newNode.hash || newNode.schemaHash,
            tooltip: newNode.tooltip,
            values: newValues,
          },
        },
      };
      Object.keys(newGraphData.nodes[nodeName].links).forEach((nodeInput) => {
        if (!newNode.inputs.some((input) => input.name === nodeInput)) {
          delete newGraphData.nodes[nodeName].links[nodeInput];
        }
      });
      setGraphData(newGraphData);
      if (save) {
        void saveGraph(newGraphData);
      }
    },
    [saveGraph],
  );

  const updateNodeLocation = useCallback(
    (nodeName: string, { x, y }: { x: number; y: number }, save = true) => {
      setNodes((oldNodes) =>
        oldNodes.map((node) =>
          node.name === nodeName ? ({ ...node, location: { x, y } } satisfies Node) : node,
        ),
      );
      const currentGraphData = graphDataRef.current;
      const newGraphData = {
        ...currentGraphData,
        nodes: {
          ...currentGraphData.nodes,
          [nodeName]: {
            ...currentGraphData.nodes[nodeName],
            location: { x, y },
          },
        },
      };
      setGraphData(newGraphData);
      if (save) {
        void saveGraph(newGraphData);
      }
    },
    [saveGraph],
  );

  const removeNode = useCallback(
    (nodeId: string, save = true) => {
      const newGraphData = { ...graphDataRef.current };
      delete newGraphData.nodes[nodeId];

      const outgoingLinks = linksRef.current.filter((link) => link.from.nodeId === nodeId);
      outgoingLinks.forEach((outgoingLink) => {
        const { nodeId: destNodeId, portId: destPortId } = outgoingLink.to;
        const destPortKey = destPortId.split('input-')[1];
        if (newGraphData.nodes[destNodeId]) {
          if (newGraphData.nodes[destNodeId].links[destPortKey].length === 1) {
            delete newGraphData.nodes[destNodeId].links[destPortKey];
          } else {
            newGraphData.nodes[destNodeId].links[destPortKey] = newGraphData.nodes[
              destNodeId
            ].links[destPortKey].filter((link) => link.sourceNode !== nodeId);
          }
        }
      });
      setGraphData(newGraphData);
      setNodes((oldNodes) => oldNodes.filter((filterNode) => filterNode.name !== nodeId));
      setLinks((oldLinks) =>
        oldLinks.filter((link) => link.to.nodeId !== nodeId && link.from.nodeId !== nodeId),
      );
      if (save) {
        void saveGraph(newGraphData);
      }
    },
    [saveGraph],
  );

  const removeSelectedNode = useCallback(
    (nodeId: string, save = true) => {
      removeNode(nodeId, save);
    },
    [removeNode],
  );

  const createLink = useCallback(
    async (link: Pick<Link, 'from' | 'to'>) => {
      const newLink = { id: crypto.randomUUID(), ...link };
      setLinks((oldLinks) => [...oldLinks, newLink]);
      const parsedToPortId = link.to.portId.split('input-')[1];
      const parsedFromPortId = link.from.portId.split('output-')[1];
      const currentGraphData = graphDataRef.current;
      const newNodeValues = { ...currentGraphData.nodes[link.to.nodeId].values };
      delete newNodeValues[parsedToPortId];
      const newGraphData = {
        ...currentGraphData,
        nodes: {
          ...currentGraphData.nodes,
          [link.to.nodeId]: {
            ...currentGraphData.nodes[link.to.nodeId],
            links: {
              ...currentGraphData.nodes[link.to.nodeId].links,
              [parsedToPortId]: [
                ...(currentGraphData.nodes[link.to.nodeId].links[parsedToPortId] || []),
                { sourceNode: link.from.nodeId, outputPort: parsedFromPortId },
              ],
            },
            values: {
              ...newNodeValues,
            },
          },
        },
      };
      setGraphData(newGraphData);
      await saveGraph(newGraphData);
    },
    [saveGraph],
  );

  const removeLink = useCallback(
    async (linkId: string) => {
      const {
        to: { nodeId: toNodeId, portId: toPortId },
        from: { nodeId: fromNodeId, portId: fromPortId },
      } = linksRef.current.find((link) => link.id === linkId);
      const parsedPortId = toPortId.split('input-')[1];

      const newGraphData = { ...graphDataRef.current };
      if (newGraphData.nodes[toNodeId]?.links[parsedPortId]?.length > 1) {
        newGraphData.nodes[toNodeId].links[parsedPortId] = newGraphData.nodes[toNodeId]?.links[
          parsedPortId
        ]?.filter(
          (link) =>
            link.sourceNode !== fromNodeId || link.outputPort !== fromPortId.split('output-')[1],
        );
      } else {
        delete newGraphData.nodes[toNodeId]?.links[toPortId.split('input-')[1]];
        newGraphData.nodes[toNodeId].values[toPortId.split('input-')[1]] = '';
      }

      setGraphData(newGraphData);
      setLinks((oldLinks) => oldLinks.filter((link) => link.id !== linkId));
      await saveGraph(newGraphData);
    },
    [saveGraph],
  );

  const removeSelectedLink = useCallback(
    (linkId: string) =>
      showConfirmation({
        onAffirm: createAsyncNotification('Removed link.', async () => {
          await removeLink(linkId);
          await validateGraphData();
        }),
        message: 'Are you sure you want to delete selected link(s)?',
      }),
    [showConfirmation, createAsyncNotification, removeLink, validateGraphData],
  );

  const updatePortValue = useCallback(
    async (nodeId: string, portId: string, value: unknown, save = true) => {
      if (!nodeId) {
        return;
      }
      const parsedPortId = portId.split('input-')[1];
      const newGraphData = { ...graphDataRef.current };
      let stringValue: string | undefined;
      if (value === null || value === undefined) {
        stringValue = undefined;
      } else if (typeof value === 'string') {
        stringValue = value;
      } else if (
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'bigint'
      ) {
        stringValue = String(value);
      } else {
        stringValue = JSON.stringify(value);
      }
      newGraphData.nodes[nodeId].values[parsedPortId] = stringValue;
      setGraphData(newGraphData);
      if (save) {
        await saveGraph(newGraphData);
      }
    },
    [saveGraph],
  );

  const duplicateNodes = useCallback(
    (nodeList: string[]) => {
      const currentLinks = linksRef.current;
      const currentGraphData = graphDataRef.current;
      let newLastNodeId = lastNodeIdRef.current;
      const newGraphData = { ...currentGraphData };
      const newLinks = [...currentLinks];
      nodeList.forEach((nodeId) => {
        const sourceNode = currentGraphData.nodes[nodeId];
        if (!sourceNode) {
          return;
        }
        const nodeName = sourceNode.name.match(/(.+)_\d+$/)?.[1] || sourceNode.name.split('_')[0];
        newLastNodeId += 1;
        const newNodeName = `${nodeName}_${newLastNodeId}`;
        const newLocation = {
          x: sourceNode.location.x + 20,
          y: sourceNode.location.y + 20,
        };
        newGraphData.nodes[newNodeName] = {
          ...sourceNode,
          name: newNodeName,
          location: newLocation,
          links: {},
          values: { ...sourceNode.values },
          ports: {
            inputs: sourceNode.ports.inputs.map((input) => ({ ...input })),
            outputs: sourceNode.ports.outputs.map((output) => ({ ...output })),
          },
        };
        const linksToDupe = currentLinks.filter(
          (link) => link.to.nodeId === sourceNode.name || link.from.nodeId === sourceNode.name,
        );
        linksToDupe.forEach((linkToDupe) => {
          if (linkToDupe.from.nodeId === sourceNode.name) {
            const newLink = {
              ...linkToDupe,
              from: { ...linkToDupe.from, nodeId: newNodeName },
              id: crypto.randomUUID(),
            };
            newLinks.push(newLink);
            newGraphData.nodes[newLink.to.nodeId].links[newLink.to.portId.split('input-')[1]].push({
              sourceNode: newNodeName,
              outputPort: newLink.from.portId.split('output-')[1],
            });
          } else {
            const newLink = {
              ...linkToDupe,
              to: { ...linkToDupe.to, nodeId: newNodeName },
              id: crypto.randomUUID(),
            };
            newLinks.push(newLink);
            if (
              !Object.keys(newGraphData.nodes[newNodeName].links).includes(
                newLink.to.portId.split('input-')[1],
              )
            ) {
              newGraphData.nodes[newNodeName].links[newLink.to.portId.split('input-')[1]] = [];
            }
            newGraphData.nodes[newNodeName].links[newLink.to.portId.split('input-')[1]].push({
              sourceNode: newLink.from.nodeId,
              outputPort: newLink.from.portId.split('output-')[1],
            });
          }
        });
      });

      setLastNodeId(newLastNodeId);
      setNodes(Object.values(newGraphData.nodes));
      setLinks(newLinks);
      setGraphData(newGraphData);
      void saveGraph(newGraphData);
    },
    [saveGraph],
  );

  const providerValue = useMemo(
    () => ({
      graphData,
      graphMetadata,
      graphMetadataLoading,
      graphParseError,
      lastCreatedNodeId,
      links,
      nodes,
      ready,
      ready$,
      validationErrors,
      createNode: onCreateNode,
      createLink,
      duplicateNodes,
      refetchGraphMetadata,
      removeLink,
      removeNode,
      removeSelectedLink,
      removeSelectedNode,
      saveGraph,
      updateNode,
      updateNodeLocation,
      updatePortValue,
    }),
    [
      graphData,
      graphMetadata,
      graphMetadataLoading,
      graphParseError,
      lastCreatedNodeId,
      links,
      nodes,
      ready,
      ready$,
      validationErrors,
      createLink,
      duplicateNodes,
      onCreateNode,
      refetchGraphMetadata,
      removeLink,
      removeNode,
      removeSelectedLink,
      removeSelectedNode,
      saveGraph,
      updateNode,
      updateNodeLocation,
      updatePortValue,
    ],
  );

  return <GraphDataContext.Provider value={providerValue}>{children}</GraphDataContext.Provider>;
};
